import React, { type ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { twJoin, twMerge } from 'tailwind-merge'
import { shouldShowChangePasswordPage } from 'shared/utils/show-change-password-page'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'
import useUser, { isAuthorizedUser } from '../hooks/use-user'
import ChevronIcon from '../icons/chevron-icon'
import LinkWithoutPrefetch from './link-without-prefetch'

const Link = ({
  href,
  children,
  className,
  external,
  ...rest
}: React.PropsWithChildren<{
  href: string
  className?: string
  external?: boolean
}>): ReactNode => {
  const LinkComponent = external ? 'a' : LinkWithoutPrefetch

  return (
    <LinkComponent
      href={href}
      className={twJoin(
        `cursor-pointer px-7 py-1.5 transition-colors hover:bg-brand-primary-subtle hover:text-brand-primary`,
        className,
      )}
      {...rest}
    >
      {children}
    </LinkComponent>
  )
}

function Avatar({
  popupClassName,
  linkClassName,
}: {
  popupClassName?: string
  linkClassName?: string
}): ReactNode | null {
  const router = useRouter()
  const { user, loading } = useUser()
  const { t } = useTranslation()

  const showChangePasswordPage = shouldShowChangePasswordPage(user)

  const isProfileActionActive = router.pathname.includes('/profile')

  if (user && !isAuthorizedUser(user)) {
    return null
  }

  if (loading || !user) {
    return (
      <div
        className={twMerge(
          'size-10 animate-pulse rounded-full bg-skeleton-primary',
          addPublicCustomClassNamePrefix('avatar-skeleton'),
        )}
      />
    )
  }

  return (
    <div className={twMerge('h-full shrink-0', addPublicCustomClassNamePrefix('avatar'))}>
      <div className="group relative h-full">
        <button
          className={twJoin(
            `flex h-full items-center gap-2.5 border-b-4 focus:outline-hidden focus-visible:border-brand-primary`,
            isProfileActionActive ? 'border-brand-primary' : `border-transparent`,
          )}
        >
          <img
            className="size-10 rounded-full object-cover"
            src={user.avatarUrl}
            alt={`${user.firstName} ${user.lastName}`}
            data-testid="header-avatar-icon"
          />
          <ChevronIcon />
        </button>
        <div className="invisible absolute top-16.25 right-0 z-50 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
          <div className="absolute -top-3 right-8 border-[6px] border-x-transparent border-t-transparent border-b-background-secondary" />
          <div
            className={twMerge(
              `flex w-68 flex-col rounded-lg bg-background-secondary py-4 text-sm wrap-break-word text-text-tertiary shadow-lg`,
              popupClassName,
            )}
          >
            {!user.workspaceOwner && (
              <Link href="/profile/user-settings" className={linkClassName}>
                {t('user.profile.menu.settings')}
              </Link>
            )}
            {showChangePasswordPage && (
              <Link href="/profile/change-password" className={linkClassName}>
                {t('user.profile.menu.change_password')}
              </Link>
            )}
            <hr className="mx-4 px-4 text-text-secondary" />
            {user.impersonator && (
              <Link external href="/dashboard/back/entry?pretend_this_user=_exit">
                {t('header.exit_impersonation')}
              </Link>
            )}
            <Link external href="/dashboard/logout" className={linkClassName}>
              {t('user.profile.menu.logout')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Avatar
